import React, { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PieChart, Loader2, Save, Plus, Trash2, Pencil, Check, X, CalendarDays,
  Users, TrendingUp, Sparkles, RefreshCw, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EDITOR_ROLES = new Set(["director_sales", "dir_operaciones", "socio_director", "dir_admin_general"]);
const today = () => new Date().toISOString().slice(0, 10);
const shiftDate = (iso, n) => {
  const [y, m, d] = (iso || today()).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
};
const prettyDay = (iso) => {
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("es-ES", {
      weekday: "long", day: "numeric", month: "long", timeZone: "UTC",
    });
  } catch { return iso; }
};

const MES_ABBR = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const prettyBucket = (label, bucket) => {
  try {
    if (bucket === "mes") {
      const [y, m] = label.split("-").map(Number);
      return `${MES_ABBR[m - 1]} ${y}`;
    }
    if (bucket === "quincena") {
      // "2026-01 · Q1 (1–15)"
      const [ym, q] = label.split(" · ");
      const [y, m] = ym.split("-").map(Number);
      return `${MES_ABBR[m - 1]} ${y} · ${q.startsWith("Q1") ? "1ª quincena" : "2ª quincena"}`;
    }
    // semana: "2026-S03"
    const [y, wk] = label.split("-S");
    return `Semana ${wk} · ${y}`;
  } catch { return label; }
};

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3" data-testid={`leads-stat-${label}`}>
    <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${tone}`}><Icon className="h-4 w-4" /></div>
    <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p><p className="text-lg font-bold tabular-nums leading-none">{value}</p></div>
  </div>
);

// ============================= Reparto diario =============================
function LeadsDaily({ canEdit }) {
  const qc = useQueryClient();
  const [fecha, setFecha] = useState(today());
  const [grid, setGrid] = useState({});
  const [saveState, setSaveState] = useState("idle"); // idle | dirty | saving | saved
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);

  const dayQ = useQuery({ queryKey: ["leads-day", fecha], queryFn: async () => (await api.get("/leads/day", { params: { fecha } })).data });
  const data = dayQ.data;
  const agents = data?.agents || [];
  const sources = (data?.sources || []).filter((s) => s.activo);

  useEffect(() => {
    if (!data) return;
    const g = {};
    for (const a of agents) { g[a.id] = {}; for (const s of sources) g[a.id][s.id] = (data.cells?.[a.id]?.[s.id]) ?? 0; }
    setGrid(g);
    dirtyRef.current = false;
    setSaveState("idle");
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }, [data]); // eslint-disable-line

  const persist = async (g) => {
    const cells = [];
    for (const a of agents) for (const s of sources) cells.push({ agente_id: a.id, fuente_id: s.id, count: g[a.id]?.[s.id] || 0 });
    setSaveState("saving");
    try {
      await api.post("/leads/day", { fecha, cells });
      dirtyRef.current = false;
      setSaveState("saved");
      qc.invalidateQueries({ queryKey: ["leads-summary"] });
      setTimeout(() => setSaveState((s) => (s === "saved" ? "idle" : s)), 2200);
    } catch (e) {
      setSaveState("dirty");
      toast.error(apiErrorMessage(e, "No se pudo guardar"));
    }
  };

  const scheduleSave = (g) => {
    if (!canEdit) return;
    dirtyRef.current = true;
    setSaveState("dirty");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { timerRef.current = null; persist(g); }, 900);
  };

  const flushSave = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (dirtyRef.current && canEdit) persist(grid);
  };

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const setCell = (aid, sid, val) => {
    const n = Math.max(0, parseInt(val || "0", 10) || 0);
    setGrid((g) => {
      const ng = { ...g, [aid]: { ...(g[aid] || {}), [sid]: n } };
      scheduleSave(ng);
      return ng;
    });
  };

  const goTo = (newFecha) => { flushSave(); setFecha(newFecha); };

  const totByAgent = (aid) => sources.reduce((sum, s) => sum + (grid[aid]?.[s.id] || 0), 0);
  const totBySource = (sid) => agents.reduce((sum, a) => sum + (grid[a.id]?.[sid] || 0), 0);
  const grandTotal = agents.reduce((sum, a) => sum + totByAgent(a.id), 0);

  const isToday = fecha === today();
  const SaveStatus = () => {
    if (!canEdit) return null;
    if (saveState === "saving") return <span className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="leads-save-status"><Loader2 className="h-3.5 w-3.5 animate-spin" />Guardando…</span>;
    if (saveState === "saved") return <span className="flex items-center gap-1.5 text-xs text-emerald-600" data-testid="leads-save-status"><CheckCircle2 className="h-3.5 w-3.5" />Guardado</span>;
    if (saveState === "dirty") return <span className="flex items-center gap-1.5 text-xs text-amber-600" data-testid="leads-save-status"><Save className="h-3.5 w-3.5" />Cambios sin guardar…</span>;
    return <span className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="leads-save-status"><CheckCircle2 className="h-3.5 w-3.5 opacity-40" />Autoguardado activo</span>;
  };

  return (
    <div className="space-y-4">
      <Card><CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <CalendarDays className="h-4 w-4 text-primary" />
          <Button variant="outline" size="icon" className="h-9 w-9" title="Semana anterior" onClick={() => goTo(shiftDate(fecha, -7))} data-testid="leads-day-prev-week"><ChevronsLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" className="h-9 w-9" title="Día anterior" onClick={() => goTo(shiftDate(fecha, -1))} data-testid="leads-day-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Input type="date" value={fecha} onChange={(e) => goTo(e.target.value)} className="h-9 w-44" data-testid="leads-day-date" />
          <Button variant="outline" size="icon" className="h-9 w-9" title="Día siguiente" onClick={() => goTo(shiftDate(fecha, 1))} data-testid="leads-day-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" className="h-9 w-9" title="Semana siguiente" onClick={() => goTo(shiftDate(fecha, 7))} data-testid="leads-day-next-week"><ChevronsRight className="h-4 w-4" /></Button>
          {!isToday && <Button variant="ghost" size="sm" className="h-9" onClick={() => goTo(today())} data-testid="leads-day-today">Hoy</Button>}
          <Button variant="outline" size="sm" className="h-9" onClick={() => { flushSave(); dayQ.refetch(); }} data-testid="leads-day-refresh"><RefreshCw className="h-4 w-4" /></Button>
          <span className="ml-1 hidden text-sm font-medium capitalize text-muted-foreground sm:inline" data-testid="leads-day-pretty">{prettyDay(fecha)}</span>
        </div>
        <SaveStatus />
      </CardContent></Card>

      {dayQ.isLoading ? <Skeleton className="h-72 w-full" /> : agents.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground" data-testid="leads-no-agents">
          No hay agentes de ventas activos. Crea usuarios con rol <strong>Travel Specialist</strong> o <strong>Director of Sales</strong>.
        </CardContent></Card>
      ) : (
        <div className="overflow-x-auto rounded-xl border" data-testid="leads-grid">
          <table className="w-full border-collapse text-left">
            <thead className="bg-zinc-50">
              <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="sticky left-0 z-10 bg-zinc-50 px-4 py-3">Agente</th>
                {sources.map((s) => <th key={s.id} className="whitespace-nowrap px-3 py-3 text-center">{s.nombre}</th>)}
                <th className="px-4 py-3 text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.id} className="border-b hover:bg-zinc-50/50" data-testid={`leads-row-${a.id}`}>
                  <td className="sticky left-0 z-10 bg-white px-4 py-2">
                    <p className="text-sm font-medium">{a.full_name}</p>
                    <p className="text-[11px] text-muted-foreground">{a.role_name}</p>
                  </td>
                  {sources.map((s) => (
                    <td key={s.id} className="px-2 py-2 text-center">
                      <input type="number" min="0" value={grid[a.id]?.[s.id] ?? 0}
                        onChange={(e) => setCell(a.id, s.id, e.target.value)} disabled={!canEdit}
                        className="h-9 w-16 rounded-md border bg-white text-center text-sm tabular-nums focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/10 disabled:bg-zinc-50 disabled:text-muted-foreground"
                        data-testid={`leads-cell-${a.id}-${s.id}`} />
                    </td>
                  ))}
                  <td className="px-4 py-2 text-center text-sm font-bold tabular-nums" data-testid={`leads-row-total-${a.id}`}>{totByAgent(a.id)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 bg-zinc-50 font-semibold">
                <td className="sticky left-0 z-10 bg-zinc-50 px-4 py-3 text-sm">Total por fuente</td>
                {sources.map((s) => <td key={s.id} className="px-3 py-3 text-center text-sm tabular-nums" data-testid={`leads-col-total-${s.id}`}>{totBySource(s.id)}</td>)}
                <td className="px-4 py-3 text-center text-base text-primary tabular-nums" data-testid="leads-grand-total">{grandTotal}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {!canEdit && <p className="text-xs text-muted-foreground" data-testid="leads-readonly-note">Solo lectura. El reparto lo editan Dir. de Ventas, Dir. de Operaciones, Socio Director y Admin.</p>}
    </div>
  );
}

// ============================= Resumen =============================
// Rangos rápidos calculados on-the-fly (formato YYYY-MM-DD).
const isoDay = (dt) => dt.toISOString().slice(0, 10);
const startOfMonth = (dt) => new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), 1));
const endOfMonth = (dt) => new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + 1, 0));
const addMonthsUTC = (dt, n) => new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + n, dt.getUTCDate()));

const SUMMARY_PRESETS = [
  { key: "este_mes", label: "Este mes", bucket: "mes",
    range: () => { const n = new Date(); return { desde: isoDay(startOfMonth(n)), hasta: isoDay(n) }; } },
  { key: "3m", label: "Últimos 3 meses", bucket: "mes",
    range: () => { const n = new Date(); const s = startOfMonth(addMonthsUTC(n, -2));
      return { desde: isoDay(s), hasta: isoDay(n) }; } },
  { key: "6m", label: "Últimos 6 meses", bucket: "mes",
    range: () => { const n = new Date(); const s = startOfMonth(addMonthsUTC(n, -5));
      return { desde: isoDay(s), hasta: isoDay(n) }; } },
  { key: "ytd", label: "YTD (año actual)", bucket: "mes",
    range: () => { const y = new Date().getUTCFullYear();
      return { desde: `${y}-01-01`, hasta: isoDay(new Date()) }; } },
  { key: "h1", label: "H1 (Ene–Jun)", bucket: "mes",
    range: () => { const y = new Date().getUTCFullYear(); return { desde: `${y}-01-01`, hasta: `${y}-06-30` }; } },
  { key: "h2", label: "H2 (Jul–Dic)", bucket: "mes",
    range: () => { const y = new Date().getUTCFullYear(); return { desde: `${y}-07-01`, hasta: `${y}-12-31` }; } },
  { key: "year", label: "Año completo", bucket: "mes",
    range: () => { const y = new Date().getUTCFullYear(); return { desde: `${y}-01-01`, hasta: `${y}-12-31` }; } },
];

function LeadsSummary() {
  // Por defecto: mes en curso, agrupado por meses naturales.
  const defaultRange = SUMMARY_PRESETS[0].range();
  const [desde, setDesde] = useState(defaultRange.desde);
  const [hasta, setHasta] = useState(defaultRange.hasta);
  const [bucket, setBucket] = useState("mes");
  const [activePreset, setActivePreset] = useState("este_mes");

  const applyPreset = (key) => {
    const p = SUMMARY_PRESETS.find((x) => x.key === key);
    if (!p) return;
    const r = p.range();
    setDesde(r.desde);
    setHasta(r.hasta);
    setBucket(p.bucket);
    setActivePreset(key);
  };

  const sumQ = useQuery({ queryKey: ["leads-summary", desde, hasta, bucket], queryFn: async () => (await api.get("/leads/summary", { params: { desde, hasta, bucket } })).data });
  const d = sumQ.data;
  const sources = d?.sources || [];
  const agentsConLeads = (d?.por_agente || []).filter((a) => a.total > 0).length;
  const topFuente = (d?.por_fuente || [])[0];
  const maxSerie = Math.max(1, ...((d?.series || []).map((s) => s.total)));

  return (
    <div className="space-y-4">
      {/* Presets rápidos */}
      <Card><CardContent className="flex flex-wrap items-center gap-2 p-3" data-testid="leads-sum-presets">
        {SUMMARY_PRESETS.map((p) => (
          <Button key={p.key} size="sm"
            variant={activePreset === p.key ? "default" : "outline"}
            onClick={() => applyPreset(p.key)} className="h-8"
            data-testid={`leads-sum-preset-${p.key}`}>
            {p.label}
          </Button>
        ))}
      </CardContent></Card>

      <Card><CardContent className="flex flex-wrap items-end gap-3 p-4">
        <div><Label className="text-[11px]">Desde</Label><Input type="date" value={desde} onChange={(e) => { setDesde(e.target.value); setActivePreset(""); }} className="h-9 w-40" data-testid="leads-sum-desde" /></div>
        <div><Label className="text-[11px]">Hasta</Label><Input type="date" value={hasta} onChange={(e) => { setHasta(e.target.value); setActivePreset(""); }} className="h-9 w-40" data-testid="leads-sum-hasta" /></div>
        <div>
          <Label className="text-[11px]">Agrupar por</Label>
          <Select value={bucket} onValueChange={(v) => { setBucket(v); setActivePreset(""); }}>
            <SelectTrigger className="h-9 w-40" data-testid="leads-sum-bucket"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mes">Mes natural</SelectItem>
              <SelectItem value="quincena">Quincena</SelectItem>
              <SelectItem value="semana">Semana</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent></Card>

      {sumQ.isLoading ? <Skeleton className="h-64 w-full" /> : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Stat icon={TrendingUp} label="Leads totales" value={d?.total ?? 0} tone="bg-emerald-100 text-emerald-700" />
            <Stat icon={Users} label="Agentes con leads" value={agentsConLeads} tone="bg-sky-100 text-sky-700" />
            <Stat icon={PieChart} label="Fuente top" value={topFuente && topFuente.total > 0 ? `${topFuente.nombre} (${topFuente.total})` : "—"} tone="bg-amber-100 text-amber-700" />
          </div>

          {/* Tabla por agente × fuente */}
          <div className="overflow-x-auto rounded-xl border" data-testid="leads-summary-table">
            <table className="w-full border-collapse text-left">
              <thead className="bg-zinc-50">
                <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="sticky left-0 z-10 bg-zinc-50 px-4 py-3">Agente</th>
                  {sources.map((s) => <th key={s.id} className="whitespace-nowrap px-3 py-3 text-center">{s.nombre}</th>)}
                  <th className="px-4 py-3 text-center">Total</th>
                </tr>
              </thead>
              <tbody>
                {(d?.por_agente || []).map((a) => (
                  <tr key={a.agente_id} className="border-b hover:bg-zinc-50/50" data-testid={`leads-sum-agent-${a.agente_id}`}>
                    <td className="sticky left-0 z-10 bg-white px-4 py-2 text-sm font-medium">{a.nombre}</td>
                    {sources.map((s) => <td key={s.id} className="px-3 py-2 text-center text-sm tabular-nums">{a.por_fuente?.[s.id] || 0}</td>)}
                    <td className="px-4 py-2 text-center text-sm font-bold tabular-nums">{a.total}</td>
                  </tr>
                ))}
                {(d?.por_agente || []).length === 0 && (
                  <tr><td colSpan={sources.length + 2} className="py-8 text-center text-sm text-muted-foreground" data-testid="leads-sum-empty">Sin datos en este periodo.</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 bg-zinc-50 font-semibold">
                  <td className="sticky left-0 z-10 bg-zinc-50 px-4 py-3 text-sm">Total por fuente</td>
                  {sources.map((s) => { const f = (d?.por_fuente || []).find((x) => x.fuente_id === s.id); return <td key={s.id} className="px-3 py-3 text-center text-sm tabular-nums">{f?.total || 0}</td>; })}
                  <td className="px-4 py-3 text-center text-base text-primary tabular-nums">{d?.total ?? 0}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Serie temporal */}
          {(d?.series || []).length > 0 && (
            <Card><CardContent className="space-y-2 p-4" data-testid="leads-series">
              <p className="text-sm font-semibold text-muted-foreground">Evolución ({bucket === "mes" ? "meses naturales" : bucket})</p>
              {(d.series).map((s) => (
                <div key={s.bucket} className="flex items-center gap-3">
                  <span className="w-40 shrink-0 text-xs text-muted-foreground" data-testid={`leads-series-label-${s.bucket}`}>{prettyBucket(s.bucket, bucket)}</span>
                  <div className="h-5 flex-1 overflow-hidden rounded bg-zinc-100">
                    <div className="h-full rounded bg-primary/80 transition-all" style={{ width: `${(s.total / maxSerie) * 100}%` }} />
                  </div>
                  <span className="w-10 text-right text-sm font-medium tabular-nums">{s.total}</span>
                </div>
              ))}
            </CardContent></Card>
          )}

          <div className="flex items-center gap-2 rounded-lg border border-dashed bg-muted/30 px-4 py-3 text-sm text-muted-foreground" data-testid="leads-conversion-note">
            <Sparkles className="h-4 w-4 text-primary" /> <span><strong>Conversión por agente</strong>: disponible cuando integremos el Pipeline (los leads se cruzarán con las ventas).</span>
          </div>
        </>
      )}
    </div>
  );
}

// ============================= Fuentes (maestro) =============================
function LeadsSources({ canEdit }) {
  const qc = useQueryClient();
  const [nuevo, setNuevo] = useState("");
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");

  const srcQ = useQuery({ queryKey: ["leads-sources"], queryFn: async () => (await api.get("/leads/sources")).data });
  const sources = srcQ.data || [];

  const addMut = useMutation({
    mutationFn: async () => (await api.post("/leads/sources", { nombre: nuevo.trim() })).data,
    onSuccess: () => { toast.success("Fuente añadida"); setNuevo(""); qc.invalidateQueries({ queryKey: ["leads-sources"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const patchMut = useMutation({
    mutationFn: async ({ id, body }) => (await api.patch(`/leads/sources/${id}`, body)).data,
    onSuccess: () => { setEditId(null); qc.invalidateQueries({ queryKey: ["leads-sources"] }); qc.invalidateQueries({ queryKey: ["leads-day"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/leads/sources/${id}`)).data,
    onSuccess: () => { toast.success("Fuente eliminada"); qc.invalidateQueries({ queryKey: ["leads-sources"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <div className="max-w-2xl space-y-3">
      {canEdit && (
        <Card><CardContent className="flex items-center gap-2 p-4">
          <Input value={nuevo} onChange={(e) => setNuevo(e.target.value)} placeholder="Nueva fuente de leads (p.ej. Travel Agent)"
            onKeyDown={(e) => e.key === "Enter" && nuevo.trim() && addMut.mutate()} data-testid="leads-source-new" />
          <Button onClick={() => addMut.mutate()} disabled={!nuevo.trim() || addMut.isPending} data-testid="leads-source-add"><Plus className="mr-1.5 h-4 w-4" />Añadir</Button>
        </CardContent></Card>
      )}
      {srcQ.isLoading ? <Skeleton className="h-40 w-full" /> : sources.map((s) => (
        <Card key={s.id} data-testid={`leads-source-${s.id}`}><CardContent className="flex items-center justify-between gap-3 p-3">
          {editId === s.id ? (
            <div className="flex flex-1 items-center gap-2">
              <Input value={editVal} onChange={(e) => setEditVal(e.target.value)} className="h-9" data-testid={`leads-source-edit-${s.id}`}
                onKeyDown={(e) => e.key === "Enter" && patchMut.mutate({ id: s.id, body: { nombre: editVal.trim() } })} />
              <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" onClick={() => patchMut.mutate({ id: s.id, body: { nombre: editVal.trim() } })} data-testid={`leads-source-save-${s.id}`}><Check className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditId(null)}><X className="h-4 w-4" /></Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{s.nombre}</span>
              {!s.activo && <Badge variant="outline" className="text-[10px] text-muted-foreground">inactiva</Badge>}
            </div>
          )}
          {canEdit && editId !== s.id && (
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Switch checked={!!s.activo} onCheckedChange={(v) => patchMut.mutate({ id: s.id, body: { activo: v } })} data-testid={`leads-source-active-${s.id}`} />Activa
              </label>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditId(s.id); setEditVal(s.nombre); }} data-testid={`leads-source-editbtn-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => { if (window.confirm(`¿Eliminar la fuente "${s.nombre}"? (el histórico se conserva)`)) delMut.mutate(s.id); }} data-testid={`leads-source-del-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          )}
        </CardContent></Card>
      ))}
    </div>
  );
}

// ============================= Página =============================
export default function LeadsPage() {
  const { user } = useAuth();
  const canEdit = EDITOR_ROLES.has(user?.role);

  return (
    <div className="space-y-5" data-testid="leads-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight">
          <PieChart className="h-6 w-6 text-primary" /> Distribución de Leads
        </h1>
        <p className="text-sm text-muted-foreground">Reparto diario de leads por agente y fuente. Resumen por mes natural, quincena o semana, con presets rápidos para YTD, Ene–Jun y más.</p>
      </div>

      <Tabs defaultValue="diario" className="w-full">
        <TabsList data-testid="leads-tabs">
          <TabsTrigger value="diario" data-testid="leads-tab-diario">Reparto diario</TabsTrigger>
          <TabsTrigger value="resumen" data-testid="leads-tab-resumen">Resumen</TabsTrigger>
          <TabsTrigger value="fuentes" data-testid="leads-tab-fuentes">Fuentes</TabsTrigger>
        </TabsList>
        <TabsContent value="diario" className="mt-4"><LeadsDaily canEdit={canEdit} /></TabsContent>
        <TabsContent value="resumen" className="mt-4"><LeadsSummary /></TabsContent>
        <TabsContent value="fuentes" className="mt-4"><LeadsSources canEdit={canEdit} /></TabsContent>
      </Tabs>
    </div>
  );
}
